import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import {
  Calendar,
  CheckCircle,
  Loader2,
  Send,
  Smartphone,
  Users,
  Shuffle,
  Bell,
  LayoutDashboard,
  Settings,
} from "lucide-react";
import { ContentPageNav } from "@/components/ContentPageNav";
import { Footer } from "@/components/Footer";
import { trackDemoRequestSubmit } from "@/lib/analytics";
import DemoGallery, { type GalleryImage } from "@/components/DemoGallery";

import adminClubBeheer from "@/assets/demo/admin-club-beheer.png";
import adminSessies from "@/assets/demo/admin-sessies.png";
import adminSessieBanen from "@/assets/demo/admin-sessie-banen.png";
import adminAanmeldingen from "@/assets/demo/admin-aanmeldingen.png";
import adminHussel from "@/assets/demo/admin-hussel.png";
import adminLeden from "@/assets/demo/admin-leden.png";
import adminBanengrid from "@/assets/demo/admin-banengrid.png";
import adminUitnodigen from "@/assets/demo/admin-uitnodigen.png";
import adminBeheerder from "@/assets/demo/admin-beheerder.png";
import adminFacturatie from "@/assets/demo/admin-facturatie.png";
import adminIndelingenGevuld from "@/assets/demo/admin-indelingen-gevuld.png";
import adminIndelingenLeeg from "@/assets/demo/admin-indelingen-leeg.png";
import adminMeldingen from "@/assets/demo/admin-meldingen.png";

const adminGalleryImages: GalleryImage[] = [
  { src: adminClubBeheer, alt: "Club beheer", caption: "Clubgegevens beheren" },
  { src: adminSessies, alt: "Sessies aanmaken", caption: "Sessies aanmaken en plannen" },
  { src: adminSessieBanen, alt: "Banen configuratie", caption: "Banen per sessie configureren" },
  { src: adminAanmeldingen, alt: "Aanmeldingen overzicht", caption: "Overzicht van alle aangemelde spelers" },
  { src: adminHussel, alt: "Hussel instellingen", caption: "Hussel-algoritme instellingen" },
  { src: adminIndelingenLeeg, alt: "Indelingen leeg", caption: "Live indelingen - wachten op spelers" },
  { src: adminIndelingenGevuld, alt: "Indelingen gevuld", caption: "Live indelingen - spelers ingedeeld op banen" },
  { src: adminLeden, alt: "Leden beheer", caption: "Leden en rollen beheren" },
  { src: adminBanengrid, alt: "Banen grid", caption: "Visuele banenconfiguratie met raster" },
  { src: adminUitnodigen, alt: "Leden uitnodigen", caption: "Nieuwe leden uitnodigen via registratielink" },
  { src: adminBeheerder, alt: "Beheerders", caption: "Beheerders en husselaars toewijzen" },
  { src: adminMeldingen, alt: "Mededelingen", caption: "Pop-up mededelingen voor clubleden" },
  { src: adminFacturatie, alt: "Facturatie", caption: "Facturen en betalingen overzicht" },
];

const playerGalleryImages: GalleryImage[] = [];

const demoFormSchema = z.object({
  name: z.string().min(2, "Naam is verplicht").max(100),
  email: z.string().email("Ongeldig e-mailadres").max(255),
  phone: z.string().min(10, "Telefoonnummer is verplicht").max(20),
  clubName: z.string().min(2, "Clubnaam is verplicht").max(100),
  preferredTime: z.string().max(500),
});

type DemoFormData = z.infer<typeof demoFormSchema>;

const Demo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<DemoFormData>({
    name: "",
    email: "",
    phone: "",
    clubName: "",
    preferredTime: "",
  });

  const updateField = (field: keyof DemoFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      const validated = demoFormSchema.parse(formData);

      setLoading(true);

      const { error } = await supabase.functions.invoke("send-demo-request", {
        body: validated,
      });

      if (error) throw error;

      trackDemoRequestSubmit(formData.clubName);
      setSubmitted(true);
      toast.success("Demo aanvraag verzonden!");
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      } else {
        toast.error(error.message || "Er ging iets mis bij het verzenden");
      }
    } finally {
      setLoading(false);
    }
  };

  const screenshots = [
    {
      title: "Speler Dashboard",
      description: "Overzichtelijk dashboard voor spelers met aankomende sessies en huidige indeling",
      icon: Smartphone,
    },
    {
      title: "Live Indeling",
      description: "Real-time weergave van baan-indelingen met speelronde-informatie",
      icon: Shuffle,
    },
    {
      title: "Sessie Beheer",
      description: "Eenvoudig sessies aanmaken en beheren met flexibele instellingen",
      icon: Calendar,
    },
    {
      title: "Speler Overzicht",
      description: "Bekijk alle aangemelde spelers met hun niveau en voorkeuren",
      icon: Users,
    },
    {
      title: "Push Notificaties",
      description: "Automatische meldingen bij nieuwe rondes en wijzigingen",
      icon: Bell,
    },
    {
      title: "Admin Dashboard",
      description: "Volledig beheer van clubs, sessies en instellingen",
      icon: LayoutDashboard,
    },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <ContentPageNav showPricing />
        <div className="container mx-auto max-w-2xl px-4 py-16 text-center">
          <div className="bg-accent/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-accent" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Demo aanvraag ontvangen!</h1>
          <p className="text-muted-foreground mb-8">
            Bedankt voor je interesse in De Hussel App. We nemen binnen 2 werkdagen contact met je op om een demo in te plannen.
          </p>
          <Button onClick={() => navigate("/")}>Terug naar home</Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ContentPageNav showPricing />

      {/* Hero */}
      <section className="py-12 md:py-16 px-4 bg-gradient-to-b from-primary/10 to-background">
        <div className="container mx-auto max-w-6xl text-center">
          <Badge className="mb-4">Gratis Demo</Badge>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            Ontdek De Hussel App
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Vraag een gratis demo aan en ontdek hoe De Hussel App jouw club kan helpen met het automatiseren van hussel-avonden.
          </p>
        </div>
      </section>

      <div className="container mx-auto max-w-6xl px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <div>
            <h2 className="text-2xl font-bold mb-6">Demo Aanvragen</h2>
            <Card className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Naam *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    placeholder="Jan Jansen"
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mailadres *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="jan@tennisclub.nl"
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefoonnummer *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="06-12345678"
                    className={errors.phone ? "border-destructive" : ""}
                  />
                  {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clubName">Clubnaam *</Label>
                  <Input
                    id="clubName"
                    value={formData.clubName}
                    onChange={(e) => updateField("clubName", e.target.value)}
                    placeholder="Tennisvereniging Voorbeeld"
                    className={errors.clubName ? "border-destructive" : ""}
                  />
                  {errors.clubName && <p className="text-sm text-destructive">{errors.clubName}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferredTime">Voorkeur dag/tijd</Label>
                  <Textarea
                    id="preferredTime"
                    value={formData.preferredTime}
                    onChange={(e) => updateField("preferredTime", e.target.value)}
                    placeholder="Geef je voorkeur dag/tijd aan voor het demo-gesprek"
                    rows={3}
                  />
                </div>

                <Button type="submit" className="w-full gap-2" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Verzenden...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Demo Aanvragen
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  We nemen binnen 2 werkdagen contact met je op.
                </p>
              </form>
            </Card>
          </div>

          {/* Screenshots */}
          <div>
            <h2 className="text-2xl font-bold mb-6">Wat kun je verwachten?</h2>
            <div className="grid gap-4">
              {screenshots.map((item, index) => (
                <Card key={index} className="p-4 flex items-start gap-4 hover:border-accent/50 transition-colors">
                  <div className="bg-accent/20 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </Card>
              ))}
            </div>

            <Card className="mt-6 p-6 bg-accent/10 border-accent/30">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Settings className="h-5 w-5 text-accent" />
                Demo Account Beschikbaar
              </h3>
              <p className="text-sm text-muted-foreground">
                Tijdens de demo krijg je toegang tot een volledig ingericht demo-account zodat je zelf kunt ervaren hoe De Hussel App werkt.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Galleries */}
      <div className="container mx-auto max-w-6xl px-4 pb-16 space-y-12">
        <DemoGallery
          title="🖥️ Club-beheerder Omgeving"
          description="Bekijk hoe de beheerderomgeving eruitziet. Sessies plannen, leden beheren, banen configureren en meer."
          images={adminGalleryImages}
        />

        <DemoGallery
          title="📱 Speler Omgeving"
          description="Bekijk de spelerervaring: aanmelden voor sessies, live indelingen bekijken en meer."
          images={playerGalleryImages}
        />
      </div>

      <Footer />
    </div>
  );
};

export default Demo;
